import styled from "@emotion/styled";
import Typography from "@mui/material/Typography";
import CircleIcon from "@mui/icons-material/Circle";
import { useEffect, useState } from "react";

import List from "components/shared/List";
import PaperWrapper from "components/shared/PaperWrapper";
import TabPanel from "components/shared/Tabs/TabPanel";
import Dropzone from "components/shared/Dropzone";
import { TabPanelProps } from "types";
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Grid,
  Stack,
  Tooltip,
  duration,
} from "@mui/material";
import { getVoiceTransfers, transferVoice } from "services/voiceTransfer";
import { useUser } from "hooks/users";

function convertSecondsToMinutesAndSeconds(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

interface TransferredVoicesProps {
  speakerId: number;
  files: {
    duration: number;
    file: string;
  }[];
}

const TransferredVoices = ({ speakerId, files }: TransferredVoicesProps) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ButtonBase
        onClick={() => setOpen(!open)}
        sx={{ justifyContent: "start" }}
      >
        <Typography>Speaker {speakerId}</Typography>
      </ButtonBase>
      <Collapse in={open}>
        <Box sx={{ pl: 2 }}>
          <Grid container spacing={2} columns={12}>
            {files.map(
              ({ duration, file }: { duration: number; file: string }) => (
                <>
                  <Grid item xs={6}>
                    <Typography>{file}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography textAlign={"right"}>
                      {convertSecondsToMinutesAndSeconds(duration)}
                    </Typography>
                  </Grid>
                </>
              )
            )}
          </Grid>
        </Box>
      </Collapse>
    </>
  );
};

export default function VoiceConversionTab() {
  const REQUIRED_DURATION = 30 * 60; //30 minutes
  const [files, setFiles] = useState<File[]>([]);
  const [fileDurations, setFileDurations] = useState<{
    [key: string]: number;
  }>({});
  const [data, setData] = useState<any>({});

  const user = useUser();

  useEffect(() => {
    //fetch from firebase
    const fetchData = async () => {
      if (user) {
        const data = await getVoiceTransfers(user!.uid);
        setData(data);
      }
    };
    fetchData();
  }, [user]);

  const handleUpload = () => {
    transferVoice(user!.uid, files);
  };

  const getFileDurations = (files: File[]) => {
    files.forEach(async (file) => {
      const objectUrl = URL.createObjectURL(file);
      let media = new Audio(objectUrl);
      media.onloadedmetadata = function () {
        URL.revokeObjectURL(objectUrl);
        setFileDurations((prevFileDurations) => ({
          ...prevFileDurations,
          [file.name]: media.duration,
        }));
      };
    });
  };

  useEffect(() => {
    if (files.length > 0) {
      getFileDurations(files);
    }
  }, [files]);

  const getTotalDuration = () => {
    let totalDuration = 0;
    files.forEach((file) => {
      totalDuration += fileDurations[file.name];
    });
    return totalDuration;
  };

  return(
    <Box sx={{ width: "100%" }}>
      <PaperWrapper elevation={6} padding="2rem">
        <Title variant="h4">Voice Conversion Coming Soon</Title>
      </PaperWrapper>
    </Box>
  )

  return (
    <Box sx={{ width: "100%" }}>
      <PaperWrapper elevation={6} padding="2rem">
        <Title variant="h4">Things to note about Voice Conversion:</Title>
        <StyledList bullets={getBullets()} />
        <Dropzone
          message="Drag and drop a speaker file here, or click to select a file."
          setFiles={setFiles}
        />
        {files.length > 0 && (
          <Box sx={{ pt: "2rem" }}>
            <Grid container spacing={2} columns={12}>
              <Grid item xs={10}>
                <Typography variant="h6">File Name</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography variant="h6" textAlign={"right"}>
                  Duration
                </Typography>
              </Grid>
              {files.map((file: File) => {
                return (
                  <>
                    <Grid item xs={10}>
                      <Typography>{file.name}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                      <Typography textAlign={"right"}>
                        {convertSecondsToMinutesAndSeconds(
                          fileDurations[file.name]
                        )}
                      </Typography>
                    </Grid>
                  </>
                );
              })}
              {/* <Grid item xs={12}>
                <Typography variant="h6">Total Duration</Typography>
              </Grid> */}
              <Grid item xs={10} textAlign={"right"}>
                <Typography variant="h6">Total Duration</Typography>
              </Grid>
              <Grid item xs={2} textAlign={"right"}>
                <Typography variant="h6">
                  {convertSecondsToMinutesAndSeconds(getTotalDuration())}/
                  {convertSecondsToMinutesAndSeconds(REQUIRED_DURATION)}
                </Typography>
              </Grid>
              <Grid item xs={10} textAlign={"right"}>
                {/* {getTotalDuration() < REQUIRED_DURATION && <Typography>{`You need at least ${convertSecondsToMinutesAndSeconds(
                  REQUIRED_DURATION - getTotalDuration()
                )} more minutes worth of audio`}</Typography>} */}
              </Grid>
              <Grid item xs={2}>
                <Tooltip
                  title={
                    getTotalDuration() < REQUIRED_DURATION && (
                      <Typography>{`You need at least ${convertSecondsToMinutesAndSeconds(
                        REQUIRED_DURATION - getTotalDuration()
                      )} more minutes worth of audio`}</Typography>
                    )
                  }
                  placement="left"
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={getTotalDuration() < REQUIRED_DURATION}
                      onClick={handleUpload}
                      fullWidth
                    >
                      Upload
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box sx={{ pt: "2rem" }}>
          {/* Previous trainings */}
          <Stack spacing={2} paddingTop={2}>
            <Typography variant="h6">Transferred Voices</Typography>
            {data.length > 0 ? (
              <Stack>
                {data.map((item: any) => (
                  <TransferredVoices
                    speakerId={item["speaker-ids"]}
                    files={item["uploaded-training-files"]}
                  />
                ))}
              </Stack>
            ) : (
              <Typography>No data</Typography>
            )}
          </Stack>
        </Box>
      </PaperWrapper>
    </Box>
  );
}

function getBullets() {
  return [
    {
      label:
        "Ensure that the audio is of high quality with clear and crisp sound, free from any background noise or disturbances.",
      icon: CircleIcon,
    },
    {
      label:
        "Verify that the recording features only one speaker throughout the entire duration, maintaining consistency and coherence.",
      icon: CircleIcon,
    },
    {
      label:
        "Upload a minimum of 30 minutes of audio, where the speaker's voice remains consistently audible and distinct.",
      icon: CircleIcon,
    },
    {
      label:
        "Upload yourself speaking English or someone else speaking English (voice cloning coming to more languages soon)",
      icon: CircleIcon,
    },
  ];
}

const Title = styled(Typography)({
  fontWeight: "400",
  marginBottom: ".5rem",
});

const StyledList = styled(List)({
  display: "flex",
  flexDirection: "column",
});
